import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Users, Plus, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TimePicker from "@/components/TimePicker";
import { ConfirmIcon } from "./helpers";

export function CrewSection({ team, items, onChange, confirmations }) {
  const crew = useMemo(() => team.filter((m) => m.member_type === "crew"), [team]);
  const assignedIds = items.map((i) => i.team_id);
  const available = crew.filter((m) => !assignedIds.includes(m.id));

  const add = (teamId) => {
    onChange([...items, { team_id: teamId, call_time: "" }]);
  };
  const update = (idx, key, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: value };
    onChange(next);
  };
  const remove = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const grouped = useMemo(() => {
    const map = {};
    items.forEach((it, idx) => {
      const person = team.find((m) => m.id === it.team_id);
      if (!person) return;
      const dept = person.department || "OTHER";
      if (!map[dept]) map[dept] = [];
      map[dept].push({ ...it, person, idx });
    });
    return map;
  }, [items, team]);

  return (
    <section className="rounded-md border border-zinc-800 bg-zinc-900/60 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-amber-500" />
          <div className="text-overline text-zinc-400">Crew</div>
          <span className="text-xs text-zinc-500 font-mono">· {items.length}</span>
        </div>
        <div>
          {available.length > 0 ? (
            <Select onValueChange={add} value="">
              <SelectTrigger
                data-testid="cs-add-crew-trigger"
                className="bg-zinc-950 border-zinc-800 text-xs h-8 w-44"
              >
                <SelectValue placeholder="+ Add crew" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
                {available.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} — {m.role || m.department}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Link
              to="/team"
              className="text-xs text-amber-500 hover:text-amber-400 inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Add crew to roster
            </Link>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-zinc-500 italic">No crew assigned yet.</div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([dept, entries]) => (
            <div key={dept}>
              <div className="text-overline text-zinc-500 mb-2">{dept}</div>
              <div className="space-y-1">
                {entries.map((e) => (
                  <div
                    key={e.team_id}
                    data-testid={`cs-crew-row-${e.team_id}`}
                    className="grid grid-cols-12 gap-2 items-center p-2 rounded-sm bg-zinc-950 border border-zinc-800"
                  >
                    <div className="col-span-12 sm:col-span-7 min-w-0">
                      <div className="flex items-center gap-2">
                        <ConfirmIcon
                          email={e.person.email}
                          teamId={e.team_id}
                          confirmations={confirmations}
                        />
                        <div className="text-sm text-zinc-100 truncate">
                          {e.person.name}
                        </div>
                      </div>
                      <div className="text-xs text-zinc-500 truncate">{e.person.role}</div>
                    </div>
                    <div className="col-span-10 sm:col-span-4">
                      <TimePicker
                        value={e.call_time}
                        onChange={(v) => update(e.idx, "call_time", v)}
                        testIdPrefix={`crew-call-${e.team_id}`}
                        size="sm"
                      />
                    </div>
                    <button
                      onClick={() => remove(e.idx)}
                      data-testid={`remove-crew-${e.team_id}`}
                      className="col-span-2 sm:col-span-1 justify-self-end p-1.5 rounded-sm text-zinc-500 hover:text-rose-400"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
